import React, { useState } from 'react';
import { Search, Loader2, Mail, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ESTADO_COLORS = {
  'Escalado': 'bg-red-100 text-red-700',
  'Cerrado': 'bg-gray-100 text-gray-600',
  'Reabierto': 'bg-amber-100 text-amber-700'
};

// Portal ID de HubSpot (mismo patrón/valor que AgentQualityCalibration.jsx y
// backend derivaciones_routes.py - si cambia el portal, actualizar en los 3 lugares).
const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '3877362';
// Formato correcto: https://app.hubspot.com/contacts/{PORTAL_ID}/record/0-5/{ticketId}
// (0-5 = object type ID para tickets en HubSpot, no el legacy /ticket/{id})
const hubspotTicketUrl = (hubspotId) => `https://app.hubspot.com/contacts/${HUBSPOT_PORTAL_ID}/record/0-5/${hubspotId}`;

const TicketSearchByEmail = ({ selectedPartnerSlug, getAuthHeaders }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [error, setError] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ email: email.trim() });
      if (selectedPartnerSlug) params.append('partner_slug', selectedPartnerSlug);

      const response = await fetch(`${API_URL}/api/atencion360/tickets-por-email?${params.toString()}`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.detail || 'Error buscando tickets');
      }
      const data = await response.json();
      setTickets(data.tickets || []);
      setSearched(true);
    } catch (err) {
      console.error('Error buscando tickets por email:', err);
      setError(err.message);
      toast.error(err.message || 'Error buscando tickets');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="ticket-search-by-email">
      <form onSubmit={handleSearch} className="flex items-center gap-2 max-w-lg">
        <div className="relative flex-1">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Buscar por email del alumno (completo o parcial)"
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="ticket-search-email-input"
          />
        </div>
        <button
          type="submit"
          disabled={loading || !email.trim()}
          className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          data-testid="ticket-search-submit-btn"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Buscar
        </button>
      </form>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="ticket-search-error">
          {error}
        </div>
      )}

      {searched && !loading && !error && tickets.length === 0 && (
        <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg" data-testid="ticket-search-empty">
          Sin tickets para ese mail
        </div>
      )}

      {tickets.length > 0 && (
        <div className="overflow-x-auto border rounded-lg" data-testid="ticket-search-results">
          <table className="w-full text-sm">
            <thead className="bg-blue-50">
              <tr>
                <th className="px-3 py-2 text-left">Asunto</th>
                <th className="px-3 py-2 text-left">Estado</th>
                <th className="px-3 py-2 text-left">Clase</th>
                <th className="px-3 py-2 text-left">Canal</th>
                <th className="px-3 py-2 text-left">Fecha</th>
                <th className="px-3 py-2 text-left">Agente</th>
                <th className="px-3 py-2 text-left">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tickets.map((t) => (
                <tr key={t.hubspot_id} className="hover:bg-blue-50/50" data-testid={`ticket-row-${t.hubspot_id}`}>
                  <td className="px-3 py-2 font-medium text-gray-900">{t.asunto}</td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${ESTADO_COLORS[t.estado] || 'bg-emerald-100 text-emerald-700'}`}>
                      {t.estado || 'N/A'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{t.clase || 'N/A'}</td>
                  <td className="px-3 py-2 text-gray-600">{t.canal || 'N/A'}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{t.fecha_creacion?.split(' ')[0] || 'N/A'}</td>
                  <td className="px-3 py-2 text-gray-600">{t.agente || 'N/A'}</td>
                  <td className="px-3 py-2">
                    {t.hubspot_id && (
                      <a
                        href={hubspotTicketUrl(t.hubspot_id)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                        data-testid={`ticket-hubspot-link-${t.hubspot_id}`}
                        title="Abrir ticket en HubSpot"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                        Abrir en HubSpot
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TicketSearchByEmail;
